import {
	PRODUCT_EVENT_INGEST_MAX_ITEMS,
	type ProductEventAction,
	type ProductFeatureKey,
} from "@signalist/shared-types";

import { resolveProductRoute } from "./route";

import type { RouteKey } from "@/routes/routeFeatures";

export interface WebProductEvent {
	readonly feature: ProductFeatureKey;
	readonly action: ProductEventAction;
	readonly surface: "web";
}
export interface AnalyticsContext {
	/** An existing in-memory auth token, used only to discard another session's queue. */
	session: string | null;
	pathname: string;
	visible?: boolean;
}
export interface AnalyticsOptions {
	getContext: () => AnalyticsContext;
	send: (events: readonly WebProductEvent[]) => Promise<unknown>;
}

export function createProductAnalytics({ getContext, send }: AnalyticsOptions) {
	let queue: WebProductEvent[] = [];
	let session: string | null = null;
	let timer: ReturnType<typeof setInterval> | undefined;
	const views = new Map<RouteKey, number>();
	const drop = () => {
		queue = [];
	};
	const eligible = () => {
		const context = getContext();

		if (session !== context.session) {
			drop();
			views.clear();
			session = context.session;
		}

		if (!session || !context.pathname.startsWith("/app/") || context.visible === false) {
			drop();

			return false;
		}

		return true;
	};
	const flush = async () => {
		if (!eligible() || !queue.length) {
			return;
		}

		// Remove before awaiting: failed batches are gone, and concurrent tracks belong to a new batch.
		const batch = queue.splice(0, PRODUCT_EVENT_INGEST_MAX_ITEMS);

		try {
			await send(batch);
		} catch {
			/* Usage recording must never interrupt the app. */
		}
	};
	const track = (feature: ProductFeatureKey, action: ProductEventAction) => {
		if (!eligible()) {
			return;
		}

		queue.push({ feature, action, surface: "web" });
		if (queue.length >= PRODUCT_EVENT_INGEST_MAX_ITEMS) {
			void flush();
		}
	};
	const trackRoute = () => {
		if (!eligible()) {
			return;
		}

		const route = resolveProductRoute(getContext().pathname);

		if (!route?.feature) {
			return;
		}

		const now = Date.now();
		const previous = views.get(route.key);

		if (previous !== undefined && now - previous < 3000) {
			return;
		}

		views.set(route.key, now);
		track(route.feature, "view");
	};
	const start = () => {
		if (timer !== undefined) {
			clearInterval(timer);
		}

		timer = setInterval(() => {
			void flush();
		}, 10000);

		return () => {
			clearInterval(timer);
			timer = undefined;
			drop();
		};
	};

	return { track, trackRoute, flush, drop, start };
}
